import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = "https://365luxuryhomes.com/api/locations";
const getToken = () => localStorage.getItem("authToken");
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// ─── Reusable Image Upload Component ──────────────────────
const ImageUpload = ({ value, onChange, onUpload, label, uploading, urlInput, onUrlChange }) => {
  const fileRef = useRef(null);

  return (
    <div className="form-control">
      {label && <label className="label py-1"><span className="label-text text-xs font-medium">{label}</span></label>}
      <div className="flex items-start gap-3">
        <div
          className="relative w-28 h-20 shrink-0 border-2 border-dashed border-base-300 rounded-lg overflow-hidden cursor-pointer hover:border-primary/50 transition-colors bg-base-200/50 group"
          onClick={() => fileRef.current?.click()}
        >
          {value ? (
            <>
              <img src={value} alt="" className="w-28 h-20 object-cover rounded-lg" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white text-xs font-medium">{uploading ? "Uploading..." : "Change"}</span>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={18} height={18} fill="none" className="text-base-content/30">
                <path d="M12 4.5V14.5M12 4.5C11.2998 4.5 9.99153 6.4943 9.5 7M12 4.5C12.7002 4.5 14.0085 6.4943 14.5 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20 16.5C20 18.982 19.482 19.5 17 19.5H7C4.518 19.5 4 18.982 4 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-[10px] text-base-content/30">Upload</span>
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])}
          />
        </div>
        {urlInput && (
          <div className="flex-1 space-y-1">
            <input
              className="input input-bordered input-sm w-full"
              placeholder="Or paste image URL..."
              value={value || ""}
              onChange={(e) => onUrlChange(e.target.value)}
            />
            {value && (
              <button
                type="button"
                className="text-[10px] text-error hover:underline"
                onClick={() => onChange("")}
              >
                Remove image
              </button>
            )}
          </div>
        )}
        {!urlInput && value && (
          <button
            type="button"
            className="text-[10px] text-error mt-1 hover:underline"
            onClick={() => onChange("")}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

const emptyLocation = {
  slug: "",
  name: "",
  tagline: "",
  image: "",
  hero: { title: "", subtitle: "" },
  intro: "",
  lifestyle: { dining: [], activities: [] },
  faq: [],
  order: 0,
};

const LocationsManager = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, object = form
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await axios.get(API);
      setLocations(res.data || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await axios.post(`${API}/upload-image`, fd, {
        headers: authHeaders(),
      });
      return res.data.url;
    } catch (e) {
      alert("Image upload failed");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!editing.name || !editing.slug) {
      alert("Name and Slug are required");
      return;
    }
    setSaving(true);
    try {
      if (editing._id) {
        await axios.put(`${API}/${editing._id}`, editing, {
          headers: authHeaders(),
        });
      } else {
        await axios.post(API, editing, { headers: authHeaders() });
      }
      setEditing(null);
      await fetchLocations();
    } catch (e) {
      alert(e.response?.data?.error || "Failed to save");
    }
    setSaving(false);
  };

  const deleteLocation = async (id) => {
    if (!window.confirm("Are you sure you want to delete this location?"))
      return;
    try {
      await axios.delete(`${API}/${id}`, { headers: authHeaders() });
      await fetchLocations();
    } catch (e) {
      alert("Failed to delete");
    }
  };

  // ─── Editing helpers ─────────────────
  const setField = (field, value) =>
    setEditing((prev) => ({ ...prev, [field]: value }));
  const setHeroField = (field, value) =>
    setEditing((prev) => ({
      ...prev,
      hero: { ...prev.hero, [field]: value },
    }));

  const setLifestyleArray = (key, value) => {
    const items = value
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    setEditing((prev) => ({
      ...prev,
      lifestyle: { ...prev.lifestyle, [key]: items },
    }));
  };

  const addFaq = () =>
    setEditing((prev) => ({
      ...prev,
      faq: [...(prev.faq || []), { q: "", a: "" }],
    }));
  const removeFaq = (idx) =>
    setEditing((prev) => ({
      ...prev,
      faq: prev.faq.filter((_, i) => i !== idx),
    }));
  const setFaqField = (idx, field, value) =>
    setEditing((prev) => {
      const faq = [...prev.faq];
      faq[idx] = { ...faq[idx], [field]: value };
      return { ...prev, faq };
    });

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  // ─── Form View ───────────────────────
  if (editing) {
    return (
      <div className="p-4 lg:p-6">
        <div className="flex items-center gap-3 mb-4">
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setEditing(null)}
          >
            &larr; Back
          </button>
          <h1 className="text-xl font-bold">
            {editing._id ? "Edit Location" : "Add Location"}
          </h1>
        </div>

        <div className="bg-base-100 rounded-lg p-4 lg:p-6 shadow-sm space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Name *</span>
              </label>
              <input
                className="input input-bordered"
                value={editing.name}
                onChange={(e) => setField("name", e.target.value)}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Slug *</span>
              </label>
              <input
                className="input input-bordered"
                value={editing.slug}
                placeholder="e.g. palm-jumeirah"
                onChange={(e) => setField("slug", e.target.value)}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Tagline</span>
              </label>
              <input
                className="input input-bordered"
                value={editing.tagline}
                onChange={(e) => setField("tagline", e.target.value)}
              />
            </div>
          </div>

          <ImageUpload
            value={editing.image}
            label="Location Image"
            uploading={uploading}
            urlInput
            onChange={(v) => setField("image", v)}
            onUrlChange={(v) => setField("image", v)}
            onUpload={async (f) => { const url = await uploadImage(f); if (url) setField("image", url); }}
          />

          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Order (sorting)</span>
            </label>
            <input
              className="input input-bordered w-24"
              type="number"
              value={editing.order || 0}
              onChange={(e) => setField("order", Number(e.target.value))}
            />
          </div>

          <div className="divider">Hero Section</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Hero Title</span>
              </label>
              <input
                className="input input-bordered"
                value={editing.hero?.title || ""}
                onChange={(e) => setHeroField("title", e.target.value)}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Hero Subtitle</span>
              </label>
              <input
                className="input input-bordered"
                value={editing.hero?.subtitle || ""}
                onChange={(e) => setHeroField("subtitle", e.target.value)}
              />
            </div>
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Intro Text</span>
            </label>
            <textarea
              className="textarea textarea-bordered h-24"
              value={editing.intro}
              onChange={(e) => setField("intro", e.target.value)}
            />
          </div>

          <div className="divider">Lifestyle</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">
                  Dining (one per line)
                </span>
              </label>
              <textarea
                className="textarea textarea-bordered h-24"
                value={(editing.lifestyle?.dining || []).join("\n")}
                onChange={(e) => setLifestyleArray("dining", e.target.value)}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">
                  Activities (one per line)
                </span>
              </label>
              <textarea
                className="textarea textarea-bordered h-24"
                value={(editing.lifestyle?.activities || []).join("\n")}
                onChange={(e) =>
                  setLifestyleArray("activities", e.target.value)
                }
              />
            </div>
          </div>

          <div className="divider">FAQ</div>
          {(editing.faq || []).map((faq, idx) => (
            <div
              key={idx}
              className="p-3 bg-base-200 rounded-lg space-y-2"
            >
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">FAQ #{idx + 1}</span>
                <button
                  className="btn btn-ghost btn-xs text-error"
                  onClick={() => removeFaq(idx)}
                >
                  Remove
                </button>
              </div>
              <input
                className="input input-bordered input-sm w-full"
                placeholder="Question"
                value={faq.q}
                onChange={(e) => setFaqField(idx, "q", e.target.value)}
              />
              <textarea
                className="textarea textarea-bordered textarea-sm w-full"
                placeholder="Answer"
                value={faq.a}
                onChange={(e) => setFaqField(idx, "a", e.target.value)}
              />
            </div>
          ))}
          <button className="btn btn-outline btn-sm" onClick={addFaq}>
            + Add FAQ
          </button>

          <div className="divider"></div>
          <div className="flex gap-3">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
            <button
              className={`btn btn-primary btn-sm ${saving ? "loading" : ""}`}
              onClick={save}
              disabled={saving || uploading}
            >
              {saving
                ? "Saving..."
                : editing._id
                ? "Update Location"
                : "Create Location"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ───────────────────────
  return (
    <div className="p-4 lg:p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Locations Manager</h1>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setEditing({ ...emptyLocation })}
        >
          + Add Location
        </button>
      </div>

      <div className="bg-base-100 rounded-lg shadow-sm overflow-x-auto">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>#</th>
              <th>Image</th>
              <th>Name</th>
              <th>Slug</th>
              <th>Tagline</th>
              <th>FAQ</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {locations.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-8 text-base-content/50">
                  No locations added yet. Click "Add Location" to create one.
                </td>
              </tr>
            ) : (
              locations.map((loc, idx) => (
                <tr key={loc._id} className="hover">
                  <td>{idx + 1}</td>
                  <td>
                    {loc.image ? (
                      <img
                        src={loc.image}
                        alt=""
                        className="w-16 h-10 object-cover rounded"
                      />
                    ) : (
                      <span className="text-xs text-base-content/40">
                        No image
                      </span>
                    )}
                  </td>
                  <td className="font-medium">{loc.name}</td>
                  <td className="text-xs font-mono">{loc.slug}</td>
                  <td className="text-sm">{loc.tagline}</td>
                  <td>{loc.faq?.length || 0}</td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        className="btn btn-ghost btn-xs"
                        onClick={() =>
                          setEditing({
                            ...emptyLocation,
                            ...loc,
                            hero: { ...emptyLocation.hero, ...loc.hero },
                            lifestyle: {
                              ...emptyLocation.lifestyle,
                              ...loc.lifestyle,
                            },
                          })
                        }
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-ghost btn-xs text-error"
                        onClick={() => deleteLocation(loc._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LocationsManager;
